import { useState } from 'react';
import { darkTheme } from '../../../lib/tokens';
import type { UserLearningData } from './api';
import { formatDate } from './format';

interface Props {
  achievements: UserLearningData['achievements'];
}

export function AchievementsSection({ achievements }: Props): JSX.Element {
  const [open, setOpen] = useState(false);
  const total = achievements.unlocked.length + achievements.milestones.length;

  return (
    <div
      className="rounded-md border p-4"
      style={{
        background: darkTheme.colors.bgSurface,
        borderColor: darkTheme.colors.borderSubtle,
      }}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-baseline justify-between gap-3 text-left"
      >
        <h3 className="text-sm font-semibold text-text-primary">Achievements & Milestones</h3>
        <span className="text-[11px] text-text-tertiary">
          {total} insgesamt {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div className="mt-3 space-y-3">
          <Sub
            title="Topic-Milestones"
            empty="Noch keinen Topic-Abschluss erreicht."
            items={achievements.milestones.map((m) => ({
              key: m.topicId,
              primary: m.topicTitle,
              secondary: formatDate(m.achievedAt),
              tone: 'success' as const,
            }))}
          />
          <Sub
            title="Badges"
            empty="Noch keine Badges freigeschaltet."
            items={achievements.unlocked.map((a) => ({
              key: a.slug,
              primary: a.slug,
              secondary: formatDate(a.unlockedAt),
              tone: 'accent' as const,
            }))}
          />
        </div>
      )}
    </div>
  );
}

interface Item {
  key: string;
  primary: string;
  secondary: string;
  tone: 'success' | 'accent';
}

function Sub({
  title,
  empty,
  items,
}: {
  title: string;
  empty: string;
  items: Item[];
}): JSX.Element {
  return (
    <div>
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
        {title}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-text-tertiary">{empty}</p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {items.map((it) => (
            <span
              key={it.key}
              className="inline-flex items-baseline gap-2 rounded-full px-2.5 py-1 text-[11px]"
              style={{
                background:
                  it.tone === 'success'
                    ? darkTheme.colors.successBg
                    : darkTheme.colors.accentSubtle,
                color:
                  it.tone === 'success' ? darkTheme.colors.success : darkTheme.colors.accentLight,
              }}
            >
              <span className="font-semibold">{it.primary}</span>
              <span className="opacity-70">· {it.secondary}</span>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
